// components/Providers/ProvidersTab.js (version mise à jour)
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import OptimizedProviderForm from './OptimizedProviderForm';

function ProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('restants');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showOptimizedForm, setShowOptimizedForm] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('prae.jwt');

  // Chargement des providers avec optimisation
  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/smtp-providers/stats`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!res.ok) throw new Error('Erreur chargement providers');

      const data = await res.json();
      setProviders(data.providers || []);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors du chargement des providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  // Callback quand des providers sont créés
  const handleProviderCreated = newProviders => {
    // Rafraîchir la liste
    fetchProviders();
    setShowOptimizedForm(false);
  };

  // Filtrage et tri des providers
  const filteredProviders = providers.filter(p =>
    p.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const sortFunctions = {
    planprogress: (a, b) =>
      (b.planProgressPercent || 0) - (a.planProgressPercent || 0),
    restants: (a, b) => (b.emailsRestants || 0) - (a.emailsRestants || 0),
    quota: (a, b) => (b.quotaUsedPercent || 0) - (a.quotaUsedPercent || 0),
    avg3: (a, b) => (b.avg3 || 0) - (a.avg3 || 0),
    avg7: (a, b) => (b.avg7 || 0) - (a.avg7 || 0),
    name: (a, b) => a.name.localeCompare(b.name),
  };

  const sortedProviders = filteredProviders.sort(
    sortFunctions[sortBy] || sortFunctions.restants
  );

  // Calcul des couleurs pour la progression
  const getProgressColor = progressPercent => {
    if (progressPercent >= 120) return 'bg-red-200';
    if (progressPercent >= 100) return 'bg-orange-100';
    if (progressPercent >= 90) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  const getProgressBarColor = progressPercent => {
    if (progressPercent >= 120) return 'bg-red-500';
    if (progressPercent >= 100) return 'bg-orange-500';
    if (progressPercent >= 90) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Pourcentage helper
  const percent = (value, total) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <div className="space-y-6">
      <ToastContainer />

      {/* En-tête avec filtres et boutons */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Barre de recherche */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rechercher
              </label>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Recherche provider SMTP…"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Tri */}
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trier par
              </label>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="planprogress">% du plan du jour</option>
                <option value="restants">Emails restants (quota)</option>
                <option value="quota">% quota utilisé</option>
                <option value="avg3">Débit 3 jours</option>
                <option value="avg7">Débit 7 jours</option>
                <option value="name">Nom Provider (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowOptimizedForm(true)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-md hover:from-purple-700 hover:to-blue-700 transition-colors font-medium shadow-sm"
            >
              ✨ Création Optimisée
            </button>

            <button
              onClick={() => setDrawerOpen(true)}
              className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors font-medium shadow-sm"
            >
              + Création Classique
            </button>

            <button
              onClick={fetchProviders}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              🔄
            </button>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-purple-600">
            {providers.length}
          </div>
          <div className="text-sm text-gray-600">Total Providers</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {providers.filter(p => (p.emailsRestants || 0) > 0).length}
          </div>
          <div className="text-sm text-gray-600">Actifs</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">
            {providers
              .reduce((sum, p) => sum + (p.emailsRestants || 0), 0)
              .toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Emails Restants</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-orange-600">
            {providers
              .reduce((sum, p) => sum + (p.sentToday || 0), 0)
              .toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Envoyés Aujourd'hui</div>
        </div>
      </div>

      {/* Liste des providers */}
      {loading ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <div className="mt-2 text-gray-500">Chargement…</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : sortedProviders.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
          {search
            ? 'Aucun provider trouvé pour cette recherche'
            : 'Aucun provider configuré'}
        </div>
      ) : (
        <div className="grid gap-4">
          {sortedProviders.map(provider => {
            const planProgress = percent(
              provider.sentToday || 0,
              provider.plannedToday || 1
            );
            const quotaProgress = percent(
              provider.sentToday || 0,
              provider.daily_quota_max || 1
            );

            return (
              <div
                key={provider.id}
                className={`bg-white rounded-lg shadow-sm border-l-4 transition-colors ${
                  planProgress >= 120
                    ? 'border-red-500'
                    : planProgress >= 100
                      ? 'border-orange-500'
                      : planProgress >= 90
                        ? 'border-yellow-500'
                        : 'border-green-500'
                } hover:shadow-md`}
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Infos principales */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {provider.name}
                        </h3>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {provider.server}
                        </span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {provider.zone || provider.domain}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Host:</span>
                          <div className="font-mono">{provider.host}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">IP:</span>
                          <div className="font-mono">
                            {provider.outbound_ip}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">From:</span>
                          <div className="truncate">{provider.from_email}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Quota/j:</span>
                          <div>
                            {(provider.daily_quota_max || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Métriques */}
                    <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
                      {/* Progression du plan */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {planProgress}%
                        </div>
                        <div className="text-xs text-gray-500">
                          Plan du jour
                        </div>
                        <div className="text-xs text-gray-400">
                          {provider.sentToday || 0} /{' '}
                          {provider.plannedToday || 0}
                        </div>
                      </div>

                      {/* Quota utilisé */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {quotaProgress}%
                        </div>
                        <div className="text-xs text-gray-500">
                          Quota utilisé
                        </div>
                        <div className="text-xs text-gray-400">
                          {provider.emailsRestants || 0} restants
                        </div>
                      </div>

                      {/* Débit moyen */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {Math.round(provider.avg3 || 0)}
                        </div>
                        <div className="text-xs text-gray-500">Débit 3j</div>
                        <div className="text-xs text-gray-400">
                          {Math.round(provider.avg7 || 0)} (7j)
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Barre de progression */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progression du plan du jour</span>
                      <span>{planProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getProgressBarColor(planProgress)}`}
                        style={{ width: `${Math.min(planProgress, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulaire optimisé (modal) */}
      {showOptimizedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-8">
            <OptimizedProviderForm
              onProviderCreated={handleProviderCreated}
              onClose={() => setShowOptimizedForm(false)}
            />
          </div>
        </div>
      )}

      {/* Drawer classique (conservé pour compatibilité) */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-40 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-semibold">
            Créer un Provider SMTP (Classique)
          </h2>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-yellow-400">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Mode classique
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Vous utilisez le mode de création classique. Pour une
                    expérience optimisée avec templates et création en masse,
                    utilisez le bouton "Création Optimisée".
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-gray-600">
            Le formulaire classique conserve l'ancienne méthode de création.
            Fermez ce drawer et utilisez "Création Optimisée" pour une
            expérience améliorée.
          </p>
        </div>
      </div>

      {/* Overlay pour le drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setDrawerOpen(false)}
        ></div>
      )}
    </div>
  );
}

export default ProvidersTab;
